import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  getUserAccessibleProjects, 
  getRelatedPeopleForUser, 
  getPersonProjectMatchDetails, 
  maskEmail 
} from '../utils/userPermissions';
import { 
  Plus, 
  Search, 
  Filter, 
  Users, 
  Target,
  Star,
  Mail,
  Phone,
  MapPin,
  Sparkles,
  Shield,
  UserCheck,
  Trash2
} from 'lucide-react';
import { api } from '../services/api';

const People = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAvailability, setFilterAvailability] = useState('all');
  const [directoryFilter, setDirectoryFilter] = useState('all'); // 'all' or 'related'

  // Initial mock data for people
  const initialPeople = [
    {
      id: 1,
      name: 'Ana Popescu',
      role: 'Senior Frontend Developer',
      team: 'Engineering',
      email: 'ana.popescu@company.com',
      phone: '+40 123 456 789',
      location: 'Bucharest, Romania',
      availability: 'Available',
      skills: ['React', 'TypeScript', 'UI/UX', 'Node.js'],
      rating: 4.8,
      experience: '5 years'
    },
    {
      id: 2,
      name: 'Mihai Ionescu',
      role: 'Backend Developer',
      team: 'Engineering',
      email: 'mihai.ionescu@company.com',
      phone: '+40 123 456 790',
      location: 'Cluj, Romania',
      availability: 'Available',
      skills: ['Python', 'Django', 'PostgreSQL', 'Redis'],
      rating: 4.6,
      experience: '4 years'
    },
    {
      id: 3,
      name: 'Ioana Muntean',
      role: 'UI/UX Designer',
      team: 'Design',
      email: 'ioana.muntean@company.com',
      phone: '+40 123 456 791',
      location: 'Bucharest, Romania',
      availability: 'Partially Available',
      skills: ['Figma', 'Adobe Creative Suite', 'Prototyping', 'User Research'],
      rating: 4.9,
      experience: '6 years'
    },
    {
      id: 4,
      name: 'Andrei Varga',
      role: 'Product Manager',
      team: 'Product',
      email: 'andrei.varga@company.com',
      phone: '+40 123 456 792',
      location: 'Timisoara, Romania',
      availability: 'Available',
      skills: ['Product Strategy', 'Agile', 'Data Analysis', 'Stakeholder Management'],
      rating: 4.7,
      experience: '7 years'
    },
    {
      id: 5,
      name: 'Elena Dumitrescu',
      role: 'DevOps Engineer',
      team: 'Engineering',
      email: 'elena.dumitrescu@company.com',
      phone: '+40 123 456 793',
      location: 'Bucharest, Romania',
      availability: 'Available',
      skills: ['Docker', 'Kubernetes', 'AWS', 'CI/CD'],
      rating: 4.5,
      experience: '3 years'
    },
    {
      id: 6,
      name: 'Vlad Popa',
      role: 'QA Engineer',
      team: 'Quality Assurance',
      email: 'vlad.popa@company.com',
      phone: '+40 123 456 794',
      location: 'Iasi, Romania',
      availability: 'Not Available',
      skills: ['Manual Testing', 'Automation', 'Selenium', 'JIRA'],
      rating: 4.4,
      experience: '2 years'
    }
  ];

  // Load people from localStorage or use initial data
  const [people, setPeople] = useState(() => {
    const savedPeople = localStorage.getItem('skillmesh_people');
    return savedPeople !== null ? JSON.parse(savedPeople) : [];
  });

  // Load projects to determine user's published project scope
  const [projects, setProjects] = useState(() => {
    const savedProjects = localStorage.getItem('skillmesh_projects');
    return savedProjects ? JSON.parse(savedProjects) : [];
  });

  // Save people to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('skillmesh_people', JSON.stringify(people));
  }, [people]);

  // Refresh data from server Neo4j database & localStorage
  useEffect(() => {
    // Fetch live from server
    api.getPeople().then(data => {
      if (Array.isArray(data)) {
        setPeople(data);
      }
    });
    api.getProjects().then(data => {
      if (Array.isArray(data)) {
        setProjects(data);
      }
    });

    const handleStorageChange = () => {
      const savedPeople = localStorage.getItem('skillmesh_people');
      if (savedPeople) {
        setPeople(JSON.parse(savedPeople));
      }
      const savedProjects = localStorage.getItem('skillmesh_projects');
      if (savedProjects) {
        setProjects(JSON.parse(savedProjects));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleStorageChange();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleDeletePerson = async (personId, personName) => {
    if (window.confirm(`Are you sure you want to permanently delete "${personName}" from Neo4j?`)) {
      await api.deletePerson(personId);
      setPeople(prev => prev.filter(p => p.id !== personId && p.email !== personId && p.name !== personId));
    }
  };

  // Compute user's accessible projects
  const userProjects = getUserAccessibleProjects(projects, currentUser, isAdmin);

  // Ensure directory list is deduplicated and contains current user
  const allDirectoryPeople = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    people.forEach(p => {
      const em = (p.email || '').toLowerCase().trim();
      const key = em || String(p.id);
      if (key && !seen.has(key)) {
        seen.add(key);
        list.push(p);
      }
    });
    if (currentUser && currentUser.email) {
      const em = currentUser.email.toLowerCase().trim();
      if (!seen.has(em)) {
        seen.add(em);
        list.push({
          id: currentUser.id || `p_${currentUser.email}`,
          name: currentUser.name || currentUser.email.split('@')[0],
          role: currentUser.title || 'Platform Member',
          team: 'Engineering',
          email: currentUser.email,
          availability: currentUser.availability || 'Available',
          skills: (currentUser.skills || []).map(s => typeof s === 'string' ? s : s.name),
          rating: 5.0,
          experience: `${currentUser.experienceYears || 1} years`,
          bio: currentUser.bio || '',
          avatar: currentUser.avatar || 'dev-1'
        });
      }
    }
    return list;
  }, [people, currentUser]);

  const relatedPeople = React.useMemo(() => {
    return getRelatedPeopleForUser(allDirectoryPeople, userProjects, currentUser, isAdmin);
  }, [allDirectoryPeople, userProjects, currentUser, isAdmin]);

  // Admin sees all people; Normal user can view All Platform Members or Direct Project Matches
  const accessiblePeople = (!isAdmin && directoryFilter === 'related') ? relatedPeople : allDirectoryPeople;

  const filteredPeople = accessiblePeople.filter(person => {
    const matchesSearch = (person.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (person.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (person.team || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterAvailability === 'all' || person.availability === filterAvailability;
    return matchesSearch && matchesFilter;
  });

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'Available':
        return isDarkMode
          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
          : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'Partially Available':
        return isDarkMode
          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
          : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold';
      case 'Not Available':
        return isDarkMode
          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
          : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
      default:
        return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const getTeamColor = (team) => {
    switch (team) {
      case 'Engineering': return 'bg-blue-500/20 text-blue-400';
      case 'Design': return 'bg-purple-500/20 text-purple-400';
      case 'Product': return 'bg-green-500/20 text-green-400';
      case 'Quality Assurance': return 'bg-orange-500/20 text-orange-400';
      default: return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-600';
    }
  };

  const stats = {
    total: accessiblePeople.length,
    fullTime: accessiblePeople.filter(p => p.availability === 'Available').length,
    partTime: accessiblePeople.filter(p => p.availability === 'Partially Available').length,
    avgRating: accessiblePeople.length > 0 
      ? (accessiblePeople.reduce((sum, p) => sum + (p.rating || 4.5), 0) / accessiblePeople.length).toFixed(1)
      : '0.0'
  };

  return (
    <div className={`space-y-6 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center">
            <h1 className="text-3xl font-bold">People</h1>
            {isAdmin ? (
              <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30">
                Admin: All Members ({accessiblePeople.length})
              </span>
            ) : (
              <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                {directoryFilter === 'related' ? `Project Matches (${accessiblePeople.length})` : `All Platform Members (${accessiblePeople.length})`}
              </span>
            )}
          </div>
          <p className={`mt-1 transition-colors duration-200 ${
            isDarkMode ? 'text-dark-300' : 'text-light-600'
          }`}>
            {isAdmin
              ? 'Administrator View: Full company directory with complete contact profiles and team assignments.'
              : 'Explore all registered members, view verified skill sets, and discover talent across the organization.'}
          </p>
        </div>
        
        {isAdmin && (
          <div className="flex space-x-3">
            <Link
              to="/skills/new"
              className="btn-secondary flex items-center"
            >
              <Target className="h-5 w-5 mr-2" />
              Add Skill
            </Link>
            <Link
              to="/people/new"
              className="btn-primary flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Person
            </Link>
          </div>
        )}
      </div>

      {/* Directory Scope Filter for non-admin members */}
      {!isAdmin && (
        <div className="flex items-center space-x-2 border-b border-dark-800 pb-3">
          <button
            onClick={() => setDirectoryFilter('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
              directoryFilter === 'all'
                ? 'bg-primary-600 text-white shadow-lg'
                : isDarkMode ? 'bg-dark-800 text-dark-300 hover:text-white' : 'bg-light-200 text-light-700'
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            <span>All Platform Members ({allDirectoryPeople.length})</span>
          </button>
          <button
            onClick={() => setDirectoryFilter('related')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
              directoryFilter === 'related'
                ? 'bg-blue-600 text-white shadow-lg'
                : isDarkMode ? 'bg-dark-800 text-dark-300 hover:text-white' : 'bg-light-200 text-light-700'
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Project Skill Matches ({relatedPeople.length})</span>
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Users className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                {isAdmin ? 'Total Members' : 'Related Talent'}
              </div>
            </div>
          </div>
        </div>

        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Users className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.fullTime}</div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                Available Now
              </div>
            </div>
          </div>
        </div>

        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <Target className="h-6 w-6 text-yellow-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {isAdmin ? stats.partTime : userProjects.length}
              </div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                {isAdmin ? 'Part-time' : 'My Active Projects'}
              </div>
            </div>
          </div>
        </div>

        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Star className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.avgRating}</div>
              <div className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-600'
              }`}>
                Avg Rating
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
            isDarkMode ? 'text-dark-400' : 'text-light-500'
          }`} />
          <input
            type="text"
            placeholder="Search people by name, role or team..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`input-field w-full pl-10 transition-all duration-200 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          />
        </div>
        
        <select
          value={filterAvailability}
          onChange={(e) => setFilterAvailability(e.target.value)}
          className={`input-field transition-all duration-200 ${
            isDarkMode ? 'dark' : 'light'
          }`}
        >
          <option value="all">All Availability</option>
          <option value="Available">Available</option>
          <option value="Partially Available">Partially Available</option>
          <option value="Not Available">Not Available</option>
        </select>
      </div>

      {/* People Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPeople.map((person) => {
          const matchInfo = getPersonProjectMatchDetails(person, userProjects);
          const isDirectTeammate = userProjects.some(p => 
            Array.isArray(p.teamMembers) && p.teamMembers.some(m => {
              const mStr = typeof m === 'string' ? m : (m?.name || m?.email);
              return mStr && (mStr.toLowerCase() === (person.name || '').toLowerCase() || mStr.toLowerCase() === (person.email || '').toLowerCase());
            })
          );

          return (
            <div key={person.id} className={`card transition-all duration-200 flex flex-col justify-between ${
              isDarkMode ? 'dark' : 'light'
            }`}>
              <div>
                {/* Person Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-0.5">{person.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{person.role}</p>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTeamColor(person.team)}`}>
                        {person.team}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getAvailabilityColor(person.availability)}`}>
                        {person.availability}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Project Relationship Badge */}
                {!isAdmin && (
                  <div className="mb-3 space-y-1">
                    {isDirectTeammate && (
                      <div className="px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 flex items-center space-x-1.5">
                        <Users className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-medium">Direct Project Collaborator</span>
                      </div>
                    )}
                    {matchInfo.matchingProjects.length > 0 && (
                      <div className="px-2.5 py-1 rounded bg-indigo-500/10 border border-indigo-500/20 text-xs text-indigo-400 flex items-center space-x-1.5">
                        <Sparkles className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          Matches: {matchInfo.matchingProjects.map(p => p.name).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Info (Masked for normal user to protect privacy) */}
                <div className="my-3">
                  {isAdmin ? (
                    <div className={`space-y-1 text-sm ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="truncate">{person.email}</span>
                      </div>
                      {person.phone && (
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span>{person.phone}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`p-2 rounded border text-xs flex items-center justify-between ${
                      isDarkMode ? 'bg-dark-900 border-dark-800 text-dark-300' : 'bg-light-100 border-light-200 text-light-600'
                    }`}>
                      <div className="flex items-center space-x-1.5">
                        <Mail className="h-3.5 w-3.5 text-gray-400" />
                        <span className="font-mono">{maskEmail(person.email)}</span>
                      </div>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 font-medium">
                        SkillMesh Protected
                      </span>
                    </div>
                  )}
                </div>

                {/* Skills */}
                <div className="mt-3">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${
                    isDarkMode ? 'text-dark-400' : 'text-light-500'
                  }`}>
                    Skills & Strengths
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {person.skills.slice(0, 4).map((skill) => {
                      const isMatchedSkill = matchInfo.matchedSkills.includes(skill.toLowerCase());
                      return (
                        <span key={skill} className={`px-2 py-0.5 rounded text-xs transition-colors duration-200 ${
                          isMatchedSkill
                            ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-medium'
                            : isDarkMode 
                              ? 'bg-dark-800 text-dark-300' 
                              : 'bg-light-200 text-light-700'
                        }`}>
                          {skill} {isMatchedSkill && '✓'}
                        </span>
                      );
                    })}
                    {person.skills.length > 4 && (
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        isDarkMode ? 'bg-dark-800 text-dark-300' : 'bg-light-200 text-light-700'
                      }`}>
                        +{person.skills.length - 4}
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className={`flex items-center space-x-2 text-xs mt-3 ${
                  isDarkMode ? 'text-dark-300' : 'text-light-600'
                }`}>
                  <div className="flex items-center space-x-1">
                    <Star className="h-3.5 w-3.5 text-yellow-400" />
                    <span>{person.rating}</span>
                  </div>
                  <span>•</span>
                  <span>{person.experience}</span>
                  <span>•</span>
                  <span>{person.location || 'Remote'}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-700/50">
                <Link
                  to={`/people/${person.id}`}
                  className="text-primary-400 hover:text-primary-300 text-sm font-medium"
                >
                  View Profile →
                </Link>
                
                <div className="flex items-center space-x-2">
                  {isAdmin && (
                    <button
                      onClick={() => handleDeletePerson(person.id, person.name)}
                      className="p-1.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                      title="Delete user from Neo4j (Admin)"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <Link
                    to={`/match?person=${person.id}`}
                    className={`px-3 py-1 rounded text-xs transition-colors duration-200 ${
                      isDarkMode 
                        ? 'bg-dark-800 hover:bg-dark-700 text-white' 
                        : 'bg-light-200 hover:bg-light-300 text-light-700'
                    }`}
                  >
                    Match to Project
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredPeople.length === 0 && (
        <div className={`text-center py-12 px-6 rounded-2xl border border-dashed transition-colors duration-200 ${
          isDarkMode ? 'border-dark-700 bg-dark-900/50 text-dark-300' : 'border-light-300 bg-light-100/50 text-light-600'
        }`}>
          {!isAdmin && userProjects.length === 0 ? (
            <div className="max-w-md mx-auto space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-white">No Related People Visible</h3>
              <p className="text-sm">
                As a normal user, your workspace only displays team members and candidates directly related to your published projects. Publish a project to define skill requirements and connect with matching talent!
              </p>
              <div>
                <Link
                  to="/projects/new"
                  className="btn-primary inline-flex items-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Publish Project to Find Talent
                </Link>
              </div>
            </div>
          ) : (
            <div className="max-w-md mx-auto space-y-4">
              <p className="mb-2">No people found matching your criteria.</p>
              {!isAdmin && (
                <p className="text-xs text-gray-400">
                  Tip: Normal users only see people whose skills overlap with your published projects ({userProjects.map(p => p.name).join(', ')}).
                </p>
              )}
              <div className="pt-2">
                <button
                  onClick={() => { setSearchTerm(''); setFilterAvailability('all'); }}
                  className="btn-secondary mr-3"
                >
                  Reset Filters
                </button>
                <Link
                  to="/projects"
                  className="btn-primary"
                >
                  Manage My Projects
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default People;
